import {AfterViewInit, Component, ElementRef, Input, OnDestroy} from '@angular/core';

type MenuPlacement = 'right' | 'above' | 'below';

interface MenuPosition {
    left: number;
    top: number;
    placement: MenuPlacement;
}

interface MenuSize {
    width: number;
    height: number;
}

interface ViewportSize {
    width: number;
    height: number;
}

const MENU_VIEWPORT_MARGIN = 16;
const MENU_WIDGET_GAP = 8;

export function calculateWidgetOptionsMenuPosition(trigger: DOMRect, menu: MenuSize,
                                                   viewport: ViewportSize,
                                                   anchorBelowTrigger = false,
                                                   widget?: DOMRect): MenuPosition {
    const margin = MENU_VIEWPORT_MARGIN;
    const gap = MENU_WIDGET_GAP;
    const maxLeft = Math.max(margin, viewport.width - margin - menu.width);
    const maxTop = Math.max(margin, viewport.height - margin - menu.height);
    const alignedLeft = clamp(trigger.right - menu.width, margin, maxLeft);
    const alignedTop = clamp(trigger.top, margin, maxTop);

    if (anchorBelowTrigger) {
        return {
            placement: 'below',
            left: alignedLeft,
            top: trigger.bottom + gap
        };
    }

    const anchor = widget || trigger;
    const rightPosition: MenuPosition = {
        placement: 'right',
        left: anchor.right + gap,
        top: alignedTop
    };
    if (rightPosition.left + menu.width <= viewport.width - margin
        && rightPosition.top + menu.height <= viewport.height - margin) {
        return rightPosition;
    }

    const abovePosition: MenuPosition = {
        placement: 'above',
        left: alignedLeft,
        top: anchor.top - gap - menu.height
    };
    if (abovePosition.top >= margin) {
        return abovePosition;
    }

    return {
        placement: 'below',
        left: alignedLeft,
        top: trigger.bottom + gap
    };
}

export function calculateWidgetOptionsMenuMaxHeight(top: number, viewportHeight: number): number {
    return Math.max(0, viewportHeight - MENU_VIEWPORT_MARGIN - top);
}

export function shouldCloseWidgetOptionsMenuOnScroll(target: EventTarget | null, menu: HTMLElement): boolean {
    return !(target instanceof Node && menu.contains(target));
}

function clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(Math.max(value, minimum), maximum);
}

@Component({
    selector: 'app-widget-options-menu',
    templateUrl: './widget-options-menu.component.html',
    styleUrls: ['./widget-options-menu.component.scss'],
    host: {
        class: 'dropdown-menu widget-options-menu',
        '[class.dropdown-menu-end]': 'alignEnd'
    }
})
export class WidgetOptionsMenuComponent implements AfterViewInit, OnDestroy {
    @Input() title: string;
    @Input() alignEnd = false;
    @Input() anchorBelowTrigger = false;

    private readonly element: HTMLElement;
    private dropdownContainer: HTMLElement;
    private widgetContainer: HTMLElement;
    private toggle: HTMLElement;
    private placeholder: Comment;
    private resizeObserver: ResizeObserver;
    private isOpen = false;

    constructor(elementRef: ElementRef<HTMLElement>) {
        this.element = elementRef.nativeElement;
    }

    ngAfterViewInit(): void {
        this.dropdownContainer = this.element.parentElement;
        this.widgetContainer = this.element.closest('.card') as HTMLElement;
        this.toggle = this.element.previousElementSibling as HTMLElement;
        this.placeholder = document.createComment('widget-options-menu');
        this.element.parentNode?.insertBefore(this.placeholder, this.element);
        this.dropdownContainer?.addEventListener('shown.bs.dropdown', this.onShown);
        this.dropdownContainer?.addEventListener('hidden.bs.dropdown', this.onHidden);
    }

    ngOnDestroy(): void {
        this.dropdownContainer?.removeEventListener('shown.bs.dropdown', this.onShown);
        this.dropdownContainer?.removeEventListener('hidden.bs.dropdown', this.onHidden);
        this.stopTrackingPosition();
        this.restoreMenuToDropdown();
        this.placeholder?.remove();
    }

    private readonly onShown = (event: Event) => {
        if (event.target !== this.toggle && event.target !== this.dropdownContainer) return;
        this.isOpen = true;
        this.toggle.classList.add('widget-options-toggle-active');
        if (this.anchorBelowTrigger) document.body.appendChild(this.element);
        this.positionMenu();
        window.addEventListener('resize', this.positionMenu);
        document.addEventListener('scroll', this.onScroll, true);
        this.resizeObserver = new ResizeObserver(this.positionMenu);
        this.resizeObserver.observe(this.element);
    };

    private readonly onHidden = (event: Event) => {
        if (event.target !== this.toggle && event.target !== this.dropdownContainer) return;
        this.toggle.classList.remove('widget-options-toggle-active');
        this.stopTrackingPosition();
        this.resetPosition();
        this.restoreMenuToDropdown();
    };

    private readonly positionMenu = () => {
        if (!this.isOpen || !this.toggle || !this.element.classList.contains('show')) return;
        this.resetPosition();
        const menuRect = this.element.getBoundingClientRect();
        const viewportPosition = calculateWidgetOptionsMenuPosition(
            this.toggle.getBoundingClientRect(),
            {width: menuRect.width, height: menuRect.height},
            {width: document.documentElement.clientWidth, height: document.documentElement.clientHeight},
            this.anchorBelowTrigger,
            this.widgetContainer?.getBoundingClientRect()
        );
        const maxHeight = calculateWidgetOptionsMenuMaxHeight(
            viewportPosition.top, document.documentElement.clientHeight);

        Object.assign(this.element.style, {
            position: 'fixed',
            inset: 'auto',
            left: `${viewportPosition.left}px`,
            top: `${viewportPosition.top}px`,
            transform: 'none',
            margin: '0',
            maxHeight: this.anchorBelowTrigger ? `${maxHeight}px` : '',
            overflowY: this.anchorBelowTrigger ? 'auto' : ''
        });
        this.element.dataset['placement'] = viewportPosition.placement;
    };

    private readonly onScroll = (event: Event) => {
        if (!this.isOpen || !shouldCloseWidgetOptionsMenuOnScroll(event.target, this.element)) return;
        (window as any).Dropdown?.getInstance(this.toggle)?.hide();
    };

    private stopTrackingPosition(): void {
        this.isOpen = false;
        this.toggle?.classList.remove('widget-options-toggle-active');
        window.removeEventListener('resize', this.positionMenu);
        document.removeEventListener('scroll', this.onScroll, true);
        this.resizeObserver?.disconnect();
    }

    private restoreMenuToDropdown(): void {
        const parent = this.placeholder?.parentNode;
        if (parent && this.element.parentNode !== parent) {
            parent.insertBefore(this.element, this.placeholder.nextSibling);
        }
    }

    private resetPosition(): void {
        this.element.style.removeProperty('position');
        this.element.style.removeProperty('inset');
        this.element.style.removeProperty('left');
        this.element.style.removeProperty('top');
        this.element.style.removeProperty('transform');
        this.element.style.removeProperty('margin');
        this.element.style.removeProperty('max-height');
        this.element.style.removeProperty('overflow-y');
        delete this.element.dataset['placement'];
    }
}

@Component({
    selector: 'app-widget-options-section',
    template: `
        <hr class="dropdown-divider">
        <div class="widget-options-section-heading">
            <h4 class="dropdown-header">{{ title }}</h4>
            <span *ngIf="infoText" class="widget-options-section-info" tabindex="0" [attr.aria-label]="title">
                <i class="bi bi-info-circle"></i>
                <span class="widget-options-section-tooltip">{{ infoText }}</span>
            </span>
        </div>
        <ng-content></ng-content>
    `,
    styles: [`
        :host { display: contents; }
        .widget-options-section-heading { display: flex; align-items: center; justify-content: space-between; }
        .widget-options-section-info { position: relative; margin-right: 1rem; color: var(--bs-gray-600); cursor: help; }
        .widget-options-section-tooltip {
            display: none;
            position: absolute;
            z-index: 2;
            right: -0.5rem;
            top: calc(100% + 0.4rem);
            width: 19rem;
            padding: 0.75rem;
            border-radius: 0.5rem;
            background: var(--bs-gray-900);
            color: white;
            font-size: 0.75rem;
            font-weight: 400;
            line-height: 1.45;
            box-shadow: 0 0.5rem 1rem rgba(12, 35, 66, 0.2);
        }
        .widget-options-section-info:hover .widget-options-section-tooltip,
        .widget-options-section-info:focus .widget-options-section-tooltip { display: block; }
    `]
})
export class WidgetOptionsSectionComponent {
    @Input() title: string;
    @Input() infoText: string;
}

@Component({
    selector: 'app-widget-option-info',
    template: `
        <i class="bi bi-info-circle"></i>
        <span class="widget-option-tooltip">
            <span>{{ description }}</span>
            <span *ngIf="unavailableMessage" class="widget-option-unavailable-message">
                {{ unavailableMessage }}
            </span>
        </span>
    `,
    styles: [`
        :host {
            position: relative;
            flex: 0 0 auto;
            color: var(--bs-secondary-color);
            cursor: help;
            line-height: 1;
        }
        .widget-option-tooltip {
            position: absolute;
            z-index: 1070;
            right: -.45rem;
            top: calc(100% + .6rem);
            width: min(390px, calc(100vw - 2rem));
            max-height: min(420px, calc(100vh - 4rem));
            padding: 1rem 1.05rem;
            border-radius: .7rem;
            background: #1f2937;
            color: #fff;
            font-size: .84rem;
            font-weight: 400;
            line-height: 1.55;
            text-align: left;
            white-space: normal;
            overflow-wrap: break-word;
            overflow-y: auto;
            box-shadow: 0 .65rem 1.6rem rgba(17, 24, 39, .25);
            opacity: 0;
            pointer-events: none;
            transform: translateY(-.25rem);
            transition: opacity .15s ease, transform .15s ease;
        }
        .widget-option-tooltip > span:first-child {
            display: block;
        }
        .widget-option-unavailable-message {
            display: block;
            margin-top: .55rem;
            padding-top: .55rem;
            border-top: 1px solid rgba(255, 255, 255, .18);
            color: #f8d98b;
        }
        :host(:hover) .widget-option-tooltip,
        :host(:focus) .widget-option-tooltip {
            opacity: 1;
            transform: translateY(0);
        }
    `],
    host: {
        tabindex: '0',
        '[attr.aria-label]': 'label'
    }
})
export class WidgetOptionInfoComponent {
    @Input() label: string;
    @Input() description: string;
    @Input() unavailableMessage?: string | null;
}
